import { useRef, useEffect } from 'react';
import Modal from './Modal';

const TV_SYMBOLS = {
  // Crypto
  BTCUSD: 'BITSTAMP:BTCUSD',
  ETHUSD: 'BITSTAMP:ETHUSD',
  LTCUSD: 'COINBASE:LTCUSD',
  BCHUSD: 'COINBASE:BCHUSD',

  // Forex majors
  EURUSD: 'FX:EURUSD',
  GBPUSD: 'FX:GBPUSD',
  USDJPY: 'FX:USDJPY',
  USDCHF: 'FX:USDCHF',
  AUDUSD: 'FX:AUDUSD',
  USDCAD: 'FX:USDCAD',
  NZDUSD: 'FX:NZDUSD',

  // Forex crosses - EUR
  EURJPY: 'FX:EURJPY',
  EURGBP: 'FX:EURGBP',
  EURAUD: 'FX:EURAUD',
  EURCHF: 'FX:EURCHF',
  EURCAD: 'FX:EURCAD',
  EURNZD: 'FX:EURNZD',

  // Forex crosses - GBP
  GBPJPY: 'FX:GBPJPY',
  GBPAUD: 'FX:GBPAUD',
  GBPCHF: 'FX:GBPCHF',
  GBPCAD: 'FX:GBPCAD',
  GBPNZD: 'FX:GBPNZD',

  // Forex crosses - AUD/NZD/CAD
  AUDJPY: 'FX:AUDJPY',
  AUDNZD: 'FX:AUDNZD',
  AUDCAD: 'FX:AUDCAD',
  AUDCHF: 'FX:AUDCHF',
  NZDJPY: 'FX:NZDJPY',
  NZDCAD: 'FX:NZDCAD',
  NZDCHF: 'FX:NZDCHF',
  CADJPY: 'FX:CADJPY',
  CADCHF: 'FX:CADCHF',
  CHFJPY: 'FX:CHFJPY',

  // Forex exotics
  USDSGD: 'FX:USDSGD',
  USDHKD: 'FX:USDHKD',
  USDMXN: 'FX:USDMXN',
  USDZAR: 'FX:USDZAR',
  USDTRY: 'FX:USDTRY',
  USDNOK: 'FX:USDNOK',
  USDSEK: 'FX:USDSEK',
  USDPLN: 'FX:USDPLN',
  USDDKK: 'FX:USDDKK',
  USDCZK: 'FX:USDCZK',
  USDHUF: 'FX:USDHUF',
  USDCNH: 'FX:USDCNH',
  USDTHB: 'FX:USDTHB',
  EURPLN: 'FX:EURPLN',
  EURNOK: 'FX:EURNOK',
  EURSEK: 'FX:EURSEK',
  EURTRY: 'FX:EURTRY',
  EURHUF: 'FX:EURHUF',
  EURCZK: 'FX:EURCZK',
  EURDKK: 'FX:EURDKK',
  EURZAR: 'FX:EURZAR',
  EURSGD: 'FX:EURSGD',
  GBPSGD: 'FX:GBPSGD',
  GBPNOK: 'FX:GBPNOK',
  GBPSEK: 'FX:GBPSEK',
  SGDJPY: 'FX:SGDJPY',
  NOKJPY: 'FX:NOKJPY',
  SEKJPY: 'FX:SEKJPY',
  TRYJPY: 'FX:TRYJPY',
  ZARJPY: 'FX:ZARJPY',
  HKDJPY: 'FX:HKDJPY',
  MXNJPY: 'FX:MXNJPY',

  // Metals
  XAUUSD: 'TVC:GOLD',
  XAGUSD: 'TVC:SILVER',
  XAUEUR: 'OANDA:XAUEUR',
  XPTUSD: 'TVC:PLATINUM',
  XPDUSD: 'TVC:PALLADIUM',
  XAUGBP: 'OANDA:XAUGBP',
  XAUAUD: 'OANDA:XAUAUD',
  XAUCHF: 'OANDA:XAUCHF',
  XAUJPY: 'OANDA:XAUJPY',
  XAGEUR: 'OANDA:XAGEUR',
  XAGGBP: 'OANDA:XAGGBP',

  // Indices
  US500: 'FOREXCOM:SPXUSD',
  USTEC: 'FOREXCOM:NSXUSD',
  US30: 'FOREXCOM:DJI',
  DE40: 'PEPPERSTONE:GER40',
  UK100: 'FOREXCOM:UKXGBP',
  JP225: 'TVC:NI225',
  AUS200: 'PEPPERSTONE:AUS200',
  HK50: 'TVC:HSI',
  FR40: 'PEPPERSTONE:FRA40',
  EU50: 'PEPPERSTONE:EUSTX50',
  ES35: 'PEPPERSTONE:ESP35',
  IT40: 'PEPPERSTONE:ITA40',
  NL25: 'PEPPERSTONE:NETH25',
  CH20: 'PEPPERSTONE:SUI20',
  SE30: 'PEPPERSTONE:SWE30',
  CHINA50: 'PEPPERSTONE:CN50',
  INDIA50: 'PEPPERSTONE:IND50',
  SING30: 'PEPPERSTONE:SING30',
  SA40: 'PEPPERSTONE:ZAF40',
  US2000: 'PEPPERSTONE:US2000',
  USDX: 'TVC:DXY',
  VIX: 'TVC:VIX',

  // Energy
  XTIUSD: 'TVC:USOIL',
  XBRUSD: 'TVC:UKOIL',
  XNGUSD: 'TVC:NGAS',
};

function inferTvSymbol(symbol) {
  // Metals/commodities start with X (XAUUSD, XAGUSD, etc.)
  if (/^X[A-Z]{2}[A-Z]{3}$/.test(symbol)) return `OANDA:${symbol}`;

  // Standard forex pairs: 6 chars, ends with known currency code
  const currencies = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'NZD', 'SGD', 'HKD',
    'MXN', 'ZAR', 'TRY', 'NOK', 'SEK', 'PLN', 'DKK', 'CZK', 'HUF', 'CNH', 'THB'];
  if (symbol.length === 6) {
    const base = symbol.slice(0, 3);
    const quote = symbol.slice(3);
    if (currencies.includes(base) && currencies.includes(quote)) return `FX:${symbol}`;
  }

  // Crypto-looking symbols ending in USD (3-5 char base + USD)
  if (symbol.endsWith('USD') && symbol.length <= 8) {
    return `BINANCE:${symbol.replace('USD', 'USDT')}`;
  }

  // Index-like short names without USD
  if (!symbol.includes('USD') && symbol.length <= 7) return `PEPPERSTONE:${symbol}`;

  // Final fallback
  return `BINANCE:${symbol.replace('USD', 'USDT')}`;
}

export default function ChartModal({ symbol, side, onClose }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!symbol || !containerRef.current) return;
    containerRef.current.innerHTML = '';

    const tvSymbol = TV_SYMBOLS[symbol] || inferTvSymbol(symbol);
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true, symbol: tvSymbol, interval: 'D', timezone: 'Etc/UTC',
      theme: 'dark', style: '1', locale: 'en', range: '6M',
      allow_symbol_change: false, calendar: false, save_image: false,
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'tradingview-widget-container';
    wrapper.style.cssText = 'height:100%;width:100%';
    wrapper.innerHTML = '<div class="tradingview-widget-container__widget" style="height:100%;width:100%"></div>';
    wrapper.appendChild(script);
    containerRef.current.appendChild(wrapper);

    return () => { if (containerRef.current) containerRef.current.innerHTML = ''; };
  }, [symbol]);

  return (
    <Modal
      open={!!symbol}
      onClose={onClose}
      title={<><i className="fa-solid fa-chart-line mr-2"></i>{symbol} {side === 'buy' ? <span className="text-success">LONG</span> : side === 'sell' ? <span className="text-error">SHORT</span> : ''} — 6M Daily</>}
      size="lg"
    >
      <div ref={containerRef} className="w-full" style={{ height: '75vh' }}></div>
    </Modal>
  );
}
